import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import {
  DevicesNeedsServicingIcon, NewMedicalReportIcon, NewMessagesIcon,
  NewPatientCardIcon, PatientAlarmIcon, PatientDischargeIcon, PatientsUnderMonitoringIcon,
  ScheduleAppointmentIcon, HighSeverityIcon,
  LowSeverityIcon, MediumSeverityIcon,
  FreeFallIcon,
  DeviceIssueIcon,
  SqiAlertIcon,
} from '../../components/icons/Icons';
import { configureBatteryIcon } from '../../utils/batteryUtils';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import Cards from '../../components/Cards';
import AppointmentsContainer from '../appointment/AppointmentsContainer';
import FilterManagementView from '../../components/FilterManagementView';
import { updatePatientInfo, resetPatientState, setFormField } from '../../slicers/PatientSlice';
import { setMenuItem } from '../patient/Slices/PatientDetailSlice';
import useAxios from '../../services/AxiosConfig';
import managementStyles from '../ManagementPages.module.css';
import styles from './OverviewDashboard.module.css';
import ChartDetailView from '../patient/modality-charts/ChartDetailView';
import { resetFilterState } from '../patient/Slices/PatientDetailFilterSlice';
import { alertPrioritySort, getAlertTimestamp, isItemActivelySilenced, getAlertColorsAndIcon, filterUnknownTechnicalAlerts, alertCountsTowardOverview } from '../../utils/alertUtils';
import { getVitalUnit } from '../../utils/unitUtils';
import { formatDateWithTime } from '../../utils/dateUtils';
import { openModal, closeModal } from '../../components/PerinHealthModelSlice';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { setSelectedRows, setSelectAll } from '../../components/PerinHealthGridSlice';
import { useDeviceReadingsWebSocket } from '../../hooks/useDeviceReadingsWebSocket';

const OverviewDashboard = () => {
  const [patientList, setPatientList] = useState([]);
  const [alertsMap, setAlertsMap] = useState({});
  const [filteredPatientList, setFilteredPatientList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [openReports, setOpenReports] = useState([]);
  const [serviceNeeded, setServiceNeeded] = useState([]);
  const [messages, setMessages] = useState([]);
  const [patientReadings, setPatientReadings] = useState({});
  const [batteryLevels, setBatteryLevels] = useState({});
  const [chartDetailVisible, setChartDetailVisible] = useState(false);
  const [chartDetailModalData, setChartDetailModalData] = useState(null);
  const [emptyFilter, setEmptyFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const patientReadingsRef = useRef(patientReadings);
  const userUnitPreference = useSelector((state) => state.userInfo.unitType);
  const userDateFormat = useSelector((state) => state.userInfo.dateFormat);

  // WebSocket subscription for device readings - subscribe to all patient account IDs
  const patientAccountIds = useMemo(() => patientList.map(patient => patient.id), [patientList]);
  const { readings: deviceReadings, lastUpdateTimestamp } = useDeviceReadingsWebSocket(patientAccountIds);

  const filterCard = useSelector((state) => state.filters.selectedCard);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const userAccountId = useSelector((state) => state.userInfo.id);
  const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
  const httpService = useAxios(axiosConfig.envURL);

  const newPatientFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const currentDate = new Date();
      const filteredPatients = patientList.filter((patient) => {
        const createdDate = new Date(patient.createdDate);
        const dateDiff = (currentDate - createdDate) / (1000 * 3600 * 24);
        return dateDiff <= 14;
      });

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [patientList]);

  const dischargeFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const filteredPatients = patientList.filter((patient) => {
        return patient.dischargeNeededFlg;
      });

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [patientList]);

  const appointmentFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const filteredAppointments = appointments.filter(appointment => new Date(appointment.appointmentEndDateTime) > new Date());

      const patientIdsWithAppointments = new Set(filteredAppointments.map(appointment => appointment.patientId));
      const filteredPatients = patientList.filter(patient => patientIdsWithAppointments.has(patient.id));

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [appointments, patientList]);

  const unreadMessageFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const patientIdsWithUnreadMessages = new Set(messages.map(message => message.senderId));
      const filteredPatients = patientList.filter(patient => patientIdsWithUnreadMessages.has(patient.id));

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [messages, patientList]);

  const activelyEnrolledFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const filteredPatients = patientList.filter((patient) => {
        return patient.activelyEnrolledFlg;
      });

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [patientList]);

  const reportFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const patientIdsWithOpenReport = new Set(
        openReports
          .map(report => report.id)
      );
      const filteredPatients = patientList.filter(patient => patientIdsWithOpenReport.has(patient.id));

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [openReports, patientList]);

  const serviceNeededFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const patientIdsWithServiceNeeded = new Set(serviceNeeded);

      // Filter the patients based on the account IDs from the array
      const filteredPatients = patientList.filter(patient => patientIdsWithServiceNeeded.has(patient.id));

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);

    }
  }, [serviceNeeded, patientList]);

  const patientAlarmsFilter = useCallback((newSelectedCard) => {
    if (newSelectedCard === null) {
      setFilteredPatientList([]);
      setEmptyFilter(false);
    } else {
      const filteredPatients = patientList.filter(patient =>
        (alertsMap[patient.id] ?? []).some(alertCountsTowardOverview)
      );

      setEmptyFilter(filteredPatients.length === 0);
      setFilteredPatientList(filteredPatients);
    }
  }, [alertsMap, patientList]);
  

  const updatePatientFilterList = useCallback(() => {
    if (filterCard === "New Patients") {
      newPatientFilter("initial");
    } else if (filterCard === "Patients to Be Discharged") {
      dischargeFilter("initial");
    } else if (filterCard === "Patient Alerts") {
      patientAlarmsFilter("initial");
    } else if (filterCard === "Patients under monitoring") {
      activelyEnrolledFilter("initial");
    } else if (filterCard === "Appointments") {
      appointmentFilter("initial");
    } else if (filterCard === "New Messages") {
      unreadMessageFilter("initial");
    } else if (filterCard === "Unresolved Medical Report") {
      reportFilter("initial");
    } else if (filterCard === "Devices Need Servicing") {
      serviceNeededFilter("initial");
    } else {
      setFilteredPatientList([]);
    }
  }, [newPatientFilter, activelyEnrolledFilter, appointmentFilter, dischargeFilter, filterCard, reportFilter, unreadMessageFilter, patientList])

  const showDischargeModal = () => {
    const patientNames =  perinHealthGrid.selectedRows.map(patient => patient.patient).join('\n');
    dispatch(
      openModal({
          activeModalId: "FilterManagementView",
          header: 'Discharge Selected Patients?',
          message: `All overdue enrollments for the following patients will be discharged:\n<i>${patientNames}</i>`,
          buttons: [
             
              { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
              { label: 'Confirm', variant: 'primary', onClickKey: 'YES' },
          ],
      })
  );
  };

  const onDischargeHandler = async () => {
    dispatch(closeModal());
    const patientIds =  perinHealthGrid.selectedRows.map(patient => patient.id).join(',');
    try {
        const response = await httpService.delete(`enrollment/patients/discharge?accountIds=${patientIds}`);
        if (response) {
            dispatch(
                showAlert({
                    header: "Success",
                    message: `Successfully discharged patients`,
                    type: 'success'
                })
            );
            await fetchData();
        }
    } catch (error) {
      dispatch(
        showAlert({
            header: "Error",
            message: `Error discharging patients`,
            type: 'danger'
        })
      );
    }
    dispatch(setSelectAll(false));
    dispatch(setSelectedRows([]));
  };

  useEffect(() => {
    dispatch(resetPatientState());
    if (filterCard !== null) {
      updatePatientFilterList();
    }
  }, []);

  useEffect(() => {
    if (filterCard == null) {
      setEmptyFilter(false);
      setFilteredPatientList([]);
    }
  }, [filterCard]);

  // Re-apply filter when patientList changes and there's an active filter
  useEffect(() => {
    if (filterCard !== null && patientList.length > 0) {
      updatePatientFilterList();
    }
  }, [patientList]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const patientResponse = await httpService.get(`enrollment/list/patient/${userAccountId}`);
      const enrollmentList = patientResponse?.data?.data ?? [];
      const patientIds = enrollmentList.map((p) => p.id);

      if (patientIds.length === 0) {
        setLoading(false);
        return;
      }

      // Create battery level promises for each patient
      const batteryLevelsPromises = patientIds.map(async (patientId) => {
        try {
          const response = await httpService.get(`/measurements/patch/metadata/latest/${patientId}`);
          if ((response.data?.success || response.data?.code === 200) && response.data?.data) {
            const batteryLevel = response.data.data.batteryLevel;
            // Convert battery level from decimal to percentage (0.87 -> 87)
            return { patientId, batteryLevel: batteryLevel ? Math.round(batteryLevel * 100) : null };
          }
          return { patientId, batteryLevel: null };
        } catch (err) {
          return { patientId, batteryLevel: null };
        }
      });

      const [
        appointmentResponse,
        messageResponse,
        nonCriticalReportResponse,
        patientReadingsResponse,
        alertResponse,
        batteryLevelsArray
      ] = await Promise.allSettled([
        httpService.get(`/appointment?physicianIds=${encodeURIComponent(userAccountId)}`),
        httpService.get(`chime/getAllMessageByUserId?userId=${userAccountId}&recipientType=PATIENT`),
        httpService.get(`/report/listByIds?accountIds=${patientIds.join(',')}`),
        httpService.get(`/device/readings/latestByIds?accountIds=${patientIds.join(',')}&calibrationReading=false`),
        httpService.post(`/alerts/list`, { accountIds: patientIds }),
        Promise.all(batteryLevelsPromises)
      ]).then(results => results.map(result => result.status === 'fulfilled' ? result.value : null));

      // Process responses one by one to maintain clarity
      const formattedPatients = formatPatients(patientResponse?.data?.data);
      setPatientList(formattedPatients);

      const formattedAlerts = formatAlerts(patientIds, alertResponse?.data?.data ?? [])
      setAlertsMap(formattedAlerts);

      const validAppointments = appointmentResponse?.data?.data?.filter(appointment => !appointment.deletedAt) || [];
      setAppointments(validAppointments);

      const unreadMessages = messageResponse?.data?.getAllMessage?.flat().filter(
        message => message.recipientId === userAccountId && message.readStatus === false
      );
      setMessages(unreadMessages || []);

      setOpenReports(nonCriticalReportResponse.data.data || []);

      // Set battery levels from metadata API results
      const batteryLevelsMap = batteryLevelsArray.reduce((acc, { patientId, batteryLevel }) => {
        acc[patientId] = batteryLevel;
        return acc;
      }, {});
      setBatteryLevels(batteryLevelsMap);

      // Process and set patient readings
      // const readings = processReadings(patientReadingsResponse.data.data, notificationResponses);
      if (patientReadingsResponse?.data?.data?.length > 0) {
        const readings = processReadings(patientReadingsResponse.data.data);
        setPatientReadings(readings);
        patientReadingsRef.current = readings;
      }

    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [httpService, userAccountId]);

  // Update patient readings when WebSocket readings are received
  useEffect(() => {
    if (deviceReadings && deviceReadings.length > 0) {
      // Process the WebSocket readings using the same processReadings function
      const processedReadings = processReadings(deviceReadings);
      
      // Merge with existing readings (WebSocket updates should take precedence)
      setPatientReadings(prevReadings => {
        const updatedReadings = { ...prevReadings };
        Object.keys(processedReadings).forEach(patientId => {
          updatedReadings[patientId] = processedReadings[patientId];
        });
        // Update the ref as well
        patientReadingsRef.current = updatedReadings;
        return updatedReadings;
      });
    }
  }, [deviceReadings, lastUpdateTimestamp]); // Include timestamp to force re-evaluation

  // Helper function to process readings
  // const processReadings = (readingsResponses, notificationResponses) => {
  const processReadings = (readingsResponses) => {
    return readingsResponses.reduce((acc, patientData) => {
      const patientId = patientData.accountId;

      if (patientData.readings?.length > 0) {
        // Group readings by modalityId
        const groupedReadings = patientData.readings.reduce((group, reading) => {
          if (!group[reading.modalityId]) {
            group[reading.modalityId] = [];
          }
          group[reading.modalityId].push(reading);
          return group;
        }, {});

        // Find posture reading
        const postureReading = Object.values(groupedReadings).find(modalityReadings => {
          const reading = modalityReadings.find(r => r.deviceId === 1) || modalityReadings[0];
          return reading.modalityId === 14 && reading.modalityName === 'Posture';
        });

        const posture = postureReading ?
          (postureReading.find(r => r.deviceId === 1) || postureReading[0]).measurement :
          null;

        // Process readings
        const patientReadings = Object.entries(groupedReadings).reduce((readings, [modalityId, modalityReadings]) => {
          const deviceOneReading = modalityReadings.find(r => r.deviceId === 1);
          const selectedReading = deviceOneReading || modalityReadings[0];

          readings[modalityId] = {
            ...selectedReading,
          };

          return readings;
        }, {});

        acc[patientId] = {
          posture,
          data: patientReadings
        };
      }

      return acc;
    }, {});
  };


  const criticalLevels = {
    3: "Low",
    2: "Moderate",
    1: "Critical",
  }

  const formatAlerts = (patientIds, alertsData) => {
    const grouped = alertsData.reduce((acc, alert) => {
      const id = alert.alert_accountId;
      if (!acc[id]) {
        acc[id] = [];
      }
      if (!isItemActivelySilenced(alert)) {
        //only show alert on dashboard if not currently silenced
        acc[id].push(alert);
      }
      return acc;
    }, {});

    const alertsMap = {};
    for (const id of patientIds) {
      alertsMap[id] = grouped[id] || [];
    }
    return alertsMap;
  };

  const formatPatients = (patientData) => {
    const userIdSet = new Set();
    let newFormattedPatientList = [];
    if (patientData.length >= 1) {
      newFormattedPatientList = patientData.reduce((acc, user) => {
        if (!userIdSet.has(user.id)) {
          userIdSet.add(user.id);
          let groupIds = [];
          let subGroupIds = [];
          let teamIds = [];
          let organizationIds = [];
          if (user.groups) {
            user.groups.forEach(group => {
              if (group.groupId) {
                groupIds.push(group.groupId);
              }
              if (group.subGroupId) {
                subGroupIds.push(group.subGroupId);
              }
              if (group.teamId) {
                teamIds.push(group.teamId);
              }
              if (group.organizationId) {
                organizationIds.push(group.organizationId);
              }
            });
          }

          acc.push({
            ...user,
            organizationIds: organizationIds,
            groupIds: groupIds,
            subGroupIds: subGroupIds,
            teamIds: teamIds,
            patient: user.lastName + ", " + user.firstName,
            patient_severity_sort: user.healthInfo["criticalLevel"] ? user.healthInfo.criticalLevel : null,
            patient_severity: user.healthInfo["criticalLevel"] ? criticalLevels[user.healthInfo.criticalLevel] : null
          });

        }
        return acc;
      }, []);
    }
    return newFormattedPatientList;
  };

  const iconMapping = {
    'Critical': { icon: <HighSeverityIcon />, text: 'High' },
    'Moderate': { icon: <MediumSeverityIcon />, text: 'Med' },
    'Low': { icon: <LowSeverityIcon />, text: 'Low' },
  };

  const handlePatientSelection = (patient) => {
    dispatch(updatePatientInfo(patient.id));
    dispatch(resetFilterState());
    dispatch(setMenuItem('Vitals'));
    navigate('/patient-detail');
  };

  const headers = [
    { key: 'patient', label: 'Patient' },
    { key: 'alerts', label: 'Alerts' },
    { key: 'device_status', label: 'Device Status' },
    { key: 'patient_severity', label: 'Patient Severity' },
    { key: 'spo2', label: 'SpO₂' },
    { key: 'hr', label: 'HR' },
    { key: 'rr', label: 'RR' },
    { key: 'temp', label: 'TEMP' },
    { key: 'weight', label: 'WEIGHT' },
    { key: 'ausc', label: 'AUSC' },
    { key: 'ecgHrv', label: 'HRV' },
    { key: 'bp', label: 'BP' },
  ];

  const convertValue = (value, modalityId) => {
    if (value === null || value === undefined || value === '') return value;

    switch (modalityId) {
      case 1: // Temperature
        return userUnitPreference === 'Imperial' ?
          parseFloat(((value * 9 / 5) + 32).toFixed(1)) :
          parseFloat(value.toFixed(1));
      case 8: // Weight
        return userUnitPreference === 'Imperial' ?
          Math.round(value * 2.20462) :
          Math.round(value);
      default:
        return value;
    }
  };

  const getMeasurement = useCallback((patientId, modalityId) => {
    // Use state directly instead of ref to trigger re-renders
    const patientData = patientReadings[patientId] || patientReadingsRef.current[patientId];

    if (!patientData || !patientData.data) {
      return { value: '', completedDate: null, modalityName: '' };
    }

    const readings = patientData.data;

    if (!readings) return { value: '', completedDate: null, modalityName: '' };

    const reading = readings[modalityId];

    if (reading) {
      const baseDetails = {
        completedDate: reading.completedDate,
        modalityName: reading.modalityName,
        modalityType: reading.modalityType,
        modalityId: reading.modalityId,
        sqiViolation: reading.SQI_violated,
        sqiNotified: reading.SQI_notified,
        sqiWarning: reading.SQI_warning_active,
        readingAccuracy: reading.SQI_reading_accuracy,
        isFastRecovery: reading.isFastRecovery ?? false,
        background_color: reading.background_color ?? 'white',
      };

      let measurementVal = reading.measurement;

      if (Array.isArray(reading.measurement)) {
        if (Array.isArray(reading.measurement[0])) {
          let averageVals = (reading.measurement.reduce((total, item) => total + item[0], 0)) / reading.measurement.length;
          measurementVal = averageVals;
        } else {
          let averageVals = (reading.measurement.reduce((total, item) => total + item, 0)) / reading.measurement.length;
          measurementVal = averageVals;
        }
      }

      switch (modalityId) {
        case 15:
          return {
            ...baseDetails,
            value: reading.SQI_reading_accuracy === 'unacceptable' ? 'unacceptable' : reading.measurement.pNN50 ? reading.measurement.pNN50 : reading.measurement,
          };
        case 7:
          return {
            value: reading.SQI_reading_accuracy === 'unacceptable' ? 'unacceptable' : Math.random() > 0.5 ? "✓" : "✗",
            ...baseDetails,
          }
        default:
          return {
            ...baseDetails,
            value: reading.SQI_reading_accuracy !== 'unacceptable' ?
              convertValue(measurementVal, modalityId) : 'unacceptable',
            unit: getVitalUnit(modalityId, userUnitPreference)
          };
      }
    } else {
      return {
        value: '',
        completedDate: null,
        modalityName: '',
        modalityType: '',
        sqiViolation: false,
        sqiNotified: false,
        sqiWarning: false,
        readingAccuracy: null,
        isFastRecovery: false,
        background_color: 'white',
        unit: getVitalUnit(modalityId, userUnitPreference)
      };
    }
  }, [patientReadings, userUnitPreference]);

  const getBackgroundColorForReading = (reading) => {
    let backgroundColor = 'transparent';
    let color = 'inherit';
    let isOutOfLimit = false;
    if (reading.readingAccuracy === "unacceptable") {
      return { color, backgroundColor, isOutOfLimit };
    } else if (reading.background_color === "yellow") {
      backgroundColor = '#f6da67';
      isOutOfLimit = true;
    } else if (reading.background_color === "green") {
      color = 'green';
    }

    return { color, backgroundColor, isOutOfLimit };
  };

  const getBackgroundColorForBP = (systolicReading, diastolicReading) => {
    let backgroundColor = 'transparent';
    let color = 'inherit';
    let isOutOfLimit = false;
    if (systolicReading.readingAccuracy === "unacceptable" || diastolicReading.readingAccuracy === "unacceptable") {
      return { color, backgroundColor, isOutOfLimit };
    }
    else if (systolicReading.background_color === "yellow" || diastolicReading.background_color === "yellow") {
      backgroundColor = '#f6da67';
      isOutOfLimit = true;
    }
    
    else if (systolicReading.background_color === "green" || diastolicReading.background_color === "green") {
      color = 'green';
    }

    return { color, backgroundColor, isOutOfLimit };
  };



  const filterCount = (type) => {
    if (type === "new") {
      return patientList.filter((patient) => {
        const currentDate = new Date();
        const createdDate = new Date(patient.createdDate);
        const dateDiff = (currentDate - createdDate) / (1000 * 3600 * 24);
        return dateDiff <= 14;
      }).length;
    }

    if (type === "discharge") {
      return patientList.filter(patient => patient.dischargeNeededFlg).length;
    }

    if (type === "appointment") {
      const filteredAppointments = appointments.filter(appointment => new Date(appointment.appointmentEndDateTime) > new Date());
      return filteredAppointments.length;
    }

    if (type === "unreadMessage") {
      const uniquePatientIds = new Set();
      messages.forEach(message => {
        if (message.senderId !== userAccountId && !message.readStatus) {
          uniquePatientIds.add(message.senderId);
        }
      });
      return uniquePatientIds.size;
    }

    if (type === "activelyEnrolled") {
      return patientList.filter(patient => patient.activelyEnrolledFlg).length;
    }

    if (type === "reports") {
      return openReports.length;
    }

    if (type === "service") {
      return serviceNeeded.length;
    }

    if (type === "alert") {
      const patientsWithAlerts = patientList.filter(patient =>
        (alertsMap[patient.id] ?? []).some(alertCountsTowardOverview)
      );
      return patientsWithAlerts.length || 0;
    }

    return 0;
  };


  const openChartDetail = useCallback((measurement, patientId) => {
    if (!measurement?.completedDate) return;
    setChartDetailModalData({
      timeStamp: measurement.completedDate,
      deviceName: measurement.modalityName || '',
      vital: measurement.modalityName || 'Reading',
      modalityType: measurement.modalityType,
      patientId,
      posture: patientReadings[patientId]?.posture ?? 'Unknown',
    });
    setChartDetailVisible(true);
  }, [patientReadings]);

  const closeChartDetail = useCallback(() => {
    setChartDetailVisible(false);
    setChartDetailModalData(null);
  }, []);

  const renderTooltip = (description, date) => (props) => (
    <Tooltip id={`tooltip-${description}`} {...props}>
      {description}
      <br />
      {formatDateWithTime(date, userDateFormat)}
    </Tooltip>
  );

  const onAlertClickHandler = (patientId, alertType) => {
    dispatch(updatePatientInfo(patientId));
    dispatch(resetFilterState());
    dispatch(setFormField({ key: "alertTypeOpen", value: alertType }));
    navigate('/patient-detail/Alarms');
  };

  function prioritySort(a, b) {
    const priorityOrder = {
      low: 1,
      medium: 2,
      high: 3,
    };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  };

  const renderMeasurementTooltip = (measurement, isAusc = false) => (props) => {
    const { completedDate, readingAccuracy } = measurement;
    const options = {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    let updatedDate = new Date(completedDate).toLocaleDateString("en-US", options);
    return <Tooltip id="tooltip-notification" {...props}>
      {readingAccuracy === 'unacceptable' && (
        <>
          {`Unacceptable signal quality, no ${isAusc ? "recording" : "metrics"} provided.`}
          <br />
        </>
      )}
      {readingAccuracy === 'acceptable' && (
        <>
          Signal quality is low, interpret with caution.
          <br />
        </>
      )}
      {updatedDate}
    </Tooltip>;
  };

  const checkSignalQuality = (readingAccuracy) => {
    if (readingAccuracy === "unacceptable") {
      return <SqiAlertIcon switchColors={true} />
    } else if (readingAccuracy === "acceptable") {
      return <span style={{ paddingRight: "5px" }}><SqiAlertIcon /></span>;
    } else {
      return null;
    }
  };

  const handleChartClick = (measurement, patientId) => {
    if (measurement?.readingAccuracy === 'unacceptable') return;
    openChartDetail(measurement, patientId);
  };

  const displayValue = (value, mode = 'round', decimalPlaces = 0) => {
    if (value === 'unacceptable') {
      return "";
    }
    if (value === '' || value === null || isNaN(value)) {
      return '-';
    }
    if (mode === 'round') {
      return Math.round(value);
    }
    if (mode === 'fixed') {
      return parseFloat(value).toFixed(decimalPlaces);
    }
    return value;
  };

  const formatBatteryLevel = (batteryLevel) => {
    if (batteryLevel === -1 || batteryLevel === null) {
      return "";
    }

    const isDecimal = batteryLevel <= 1;
    const level = isDecimal ? Math.round(batteryLevel * 100) : Math.round(batteryLevel);
    const icon = configureBatteryIcon(batteryLevel);
    
    if (!icon) {
      return "";
    }
    
    return <span style={{ display: "flex", alignItems: "center", columnGap: "5px" }}>
      {icon}{`${level}%`}
    </span>
  };

  const gridData = useMemo(() => (filteredPatientList.length || emptyFilter ? filteredPatientList : patientList).map(patient => {
    if (loading) {
      return [];
    }
    const measurements = {
      temp: getMeasurement(patient.id, 1),
      hr: getMeasurement(patient.id, 4),
      spo2: getMeasurement(patient.id, 3),
      systolic: getMeasurement(patient.id, 5),
      rr: getMeasurement(patient.id, 6),
      weight: getMeasurement(patient.id, 8),
      ausc: getMeasurement(patient.id, 7),
      diastolic: getMeasurement(patient.id, 10),
      ecgHrv: getMeasurement(patient.id, 15),
    };

    let alertIcons = [];
    const patientAlerts = alertsMap[patient.id] ?? [];
    let alertCount = patientAlerts.filter(alertCountsTowardOverview).length;

    const freeFallMatch = alertsMap[patient.id]?.find(
      (item) => item.alert_subType === "fall status"
    );
    if (freeFallMatch) {
      const iconColor = getAlertColorsAndIcon(freeFallMatch.alert_color, false, true);
      alertIcons.push({
        alert: [freeFallMatch],
        icon: <FreeFallIcon color={iconColor} />,
        priority: freeFallMatch.alert_priority,
        date: freeFallMatch.alert_lastUpdatedTimestamp,
        description: freeFallMatch.alert_hover_display,
        type: "clinical"
      })
    }

    const clinicalAlerts = alertsMap[patient.id]?.filter((item => item.alert_category === "clinical" && item.alert_subType !== "fall status"));
    if (clinicalAlerts?.length > 0) {
      const sortedClinicalAlerts = clinicalAlerts.sort(alertPrioritySort);
      const icon = getAlertColorsAndIcon(sortedClinicalAlerts[0], true);
      const timestamp = getAlertTimestamp(sortedClinicalAlerts[0])
      alertIcons.push({
        alerts: clinicalAlerts,
        icon,
        date: timestamp,
        priority: sortedClinicalAlerts[0].alert_priority,
        description: sortedClinicalAlerts[0].alert_hover_display ?? "Check patient vitals",
        type: "clinical"
      })
    } 


    const technicalAlerts = alertsMap[patient.id]?.filter((item => item.alert_category === "technical"));
    if (technicalAlerts?.length > 0) {
      const memoryFull = technicalAlerts.find((item => item.alert_name?.toLowerCase().includes("memory full")));
      if (memoryFull) {
        const icon = getAlertColorsAndIcon(memoryFull, true);
        alertIcons.push({
          alerts: [memoryFull],
          icon,
          priority: memoryFull.alert_priority,
          description: memoryFull.alert_hover_display ?? "Perin Health Patch memory is full.",
          date: memoryFull.createdDate,
          type: "technical"
        })
      }
      const selfTest = technicalAlerts.find((item => item.alert_name?.toLowerCase().includes("self test")));
      if (selfTest) {
        const icon = getAlertColorsAndIcon(selfTest, true);
        alertIcons.push({
          alerts: [selfTest],
          icon,
          priority: selfTest.alert_priority,
          description: selfTest.alert_hover_display ?? "Device self-test failed. There is a technical failure with the Perin Health Patch.",
          date: selfTest.createdDate,
          type: "technical"
        })
      }
      const leadOff = technicalAlerts.find((item => item.alert_name?.toLowerCase().includes("lead off")));
      if (leadOff) {
        const icon = getAlertColorsAndIcon(leadOff, true);
        alertIcons.push({
          alerts: [leadOff],
          icon,
          priority: leadOff.alert_priority,
          description: leadOff.alert_hover_display ?? "ECG lead-off detected. Subject not wearing device.",
          date: leadOff.alert_lastUpdatedTimestamp,
          type: "technical"
        })
      }
      const motion = technicalAlerts.find((item => item.alert_hover_display?.toLowerCase().includes("excessive motion")));
      if (motion) {
        const icon = getAlertColorsAndIcon(motion, true);
        alertIcons.push({
          alerts: [motion],
          icon,
          priority: motion.alert_priority,
          description: motion.alert_hover_display ?? "Patient was active, metrics only displayed when subject is stationary.",
          date: motion.createdDate,
          type: "technical"
        })
      }

      const unknownTechnicalAlerts = filterUnknownTechnicalAlerts(technicalAlerts);
      if (unknownTechnicalAlerts?.length > 0) {
        const sortedTechnicalAlerts = unknownTechnicalAlerts.sort(alertPrioritySort);
        const iconColor = getAlertColorsAndIcon(sortedTechnicalAlerts[0].alert_color, false, true);
        const timestamp = getAlertTimestamp(sortedTechnicalAlerts[0]);
        alertIcons.push({
          alerts: technicalAlerts,
          icon: <DeviceIssueIcon color={iconColor} />,
          priority: sortedTechnicalAlerts[0].alert_priority,
          description: sortedTechnicalAlerts[0].alert_hover_display ?? "Patient has active technical alerts",
          date: timestamp,
          type: "technical"
        })
      }
    }

    const batteryLevel = batteryLevels[patient.id];

    // Initialize the flag for the row
    let rowHasOutOfLimit = false;

    const spo2Result = getBackgroundColorForReading(measurements.spo2);
    rowHasOutOfLimit = rowHasOutOfLimit || spo2Result.isOutOfLimit;

    const hrResult = getBackgroundColorForReading(measurements.hr);
    rowHasOutOfLimit = rowHasOutOfLimit || hrResult.isOutOfLimit;

    const tempResult = getBackgroundColorForReading(measurements.temp);
    rowHasOutOfLimit = rowHasOutOfLimit || tempResult.isOutOfLimit;

    const weightResult = getBackgroundColorForReading(measurements.weight);
    rowHasOutOfLimit = rowHasOutOfLimit || weightResult.isOutOfLimit;

    const auscResult = getBackgroundColorForReading(measurements.ausc);
    rowHasOutOfLimit = rowHasOutOfLimit || auscResult.isOutOfLimit;

    const ecgHrvResult = getBackgroundColorForReading(measurements.ecgHrv);
    rowHasOutOfLimit = rowHasOutOfLimit || ecgHrvResult.isOutOfLimit;

    const rrResult = getBackgroundColorForReading(measurements.rr);
    rowHasOutOfLimit = rowHasOutOfLimit || rrResult.isOutOfLimit;

    const bpResult = getBackgroundColorForBP(measurements.systolic, measurements.diastolic);
    rowHasOutOfLimit = rowHasOutOfLimit || bpResult.isOutOfLimit;

    const barStatus = !rowHasOutOfLimit && alertCount === 0;


    return {
      ...patient,
      bar_status: barStatus,
      alerts: (
        <span style={{ display: "flex", alignItems: "center", columnGap: "10px" }}>
          {alertIcons.slice(0,3).sort(prioritySort).map((item, index) => (
            <OverlayTrigger
              key={item.description}
              placement="top"
              delay={{ show: 50, hide: 50 }}
              overlay={renderTooltip(item.description, item.date)}
            >
              <button
                style={{ background: "transparent", border: 'none', padding: 0 }}
                onClick={() => onAlertClickHandler(patient.id, item.type)}
              >
                {item.icon}
              </button>
            </OverlayTrigger>
          ))}
        </span>
      ),
      alerts_sort: alertCount,
      device_status: (
        <span>
          {formatBatteryLevel(batteryLevel)}
        </span>
      ),
      device_status_sort: batteryLevel,
      spo2: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.spo2)}
          data-bgcolor={spo2Result.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.spo2, patient.id)}
            style={{ color: spo2Result.color, cursor: 'default', }}
          >
            {checkSignalQuality(measurements.spo2.readingAccuracy)}
            {displayValue(measurements.spo2.value)}
            {(measurements.spo2.value && measurements.spo2.readingAccuracy !== "unacceptable") ? measurements.spo2.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      spo2_sort: measurements.spo2.value ? Math.round(measurements.spo2.value,) : '',
      hr: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.hr)}
          data-bgcolor={hrResult.backgroundColor}
        >
          <div
            onClick={() => handleChartClick(measurements.hr, patient.id)}
            style={{ color: hrResult.color, cursor: "default" }}
          >
            {checkSignalQuality(measurements.hr.readingAccuracy)}
            {displayValue(measurements.hr.value)}
            {(measurements.hr.value && measurements.hr.readingAccuracy !== "unacceptable") ? measurements.hr.unit : ''}
          </div>
        </OverlayTrigger>
      ),
      hr_sort: measurements.hr.value ? Math.round(measurements.hr.value) : '',
      temp: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.temp)}
          data-bgcolor={tempResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.temp, patient.id)}
            style={{ color: tempResult.color, cursor: "default" }}
          >
            {checkSignalQuality(measurements.temp.readingAccuracy)}
            {displayValue(measurements.temp.value, 'fixed', 1)}
            {(measurements.temp.value && measurements.temp.readingAccuracy !== "unacceptable") ? measurements.temp.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      temp_sort: measurements.temp.value ? parseFloat(measurements.temp.value).toFixed(1) : '',
      weight: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.weight)}
          data-bgcolor={weightResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.weight, patient.id)}
            style={{ color: weightResult.color, cursor: "default" }}
          >
            {displayValue(measurements.weight.value, 'fixed', 0)}{measurements.weight.value ? measurements.weight.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      weight_sort: measurements.weight.value ? measurements.weight.value : '',
      ausc: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.ausc, true)}
          data-bgcolor={auscResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.ausc, patient.id)}
            style={{ color: auscResult.color, cursor: "default" }}
          >
            {checkSignalQuality(measurements.ausc.readingAccuracy)}
            {displayValue(measurements.ausc.value)}
            {(measurements.ausc.value && measurements.ausc.readingAccuracy !== "unacceptable") ? measurements.ausc.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      ausc_sort: measurements.ausc.value,
      ecgHrv: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.ecgHrv)}
          data-bgcolor={ecgHrvResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.ecgHrv, patient.id)}
            style={{ color: ecgHrvResult.color, cursor: "default" }}
          >
            {checkSignalQuality(measurements.ecgHrv.readingAccuracy)}
            {displayValue(measurements.ecgHrv.value)}
            {(measurements.ecgHrv.value && measurements.ecgHrv.readingAccuracy !== "unacceptable") ? measurements.ecgHrv.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      ecgHrv_sort: measurements.ecgHrv.value ? Math.round(measurements.ecgHrv.value) : '',
      rr: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.rr)}
          data-bgcolor={rrResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.rr, patient.id)}
            style={{ color: rrResult.color, cursor: "default" }}
          >
            {checkSignalQuality(measurements.rr.readingAccuracy)}
            {displayValue(measurements.rr.value)}
            {(measurements.rr.value && measurements.rr.readingAccuracy !== "unacceptable") ? measurements.rr.unit : ''}
          </span>
        </OverlayTrigger>
      ),
      rr_sort: measurements.rr.value ? Math.round(measurements.rr.value) : '',
      bp: (
        <OverlayTrigger
          overlay={renderMeasurementTooltip(measurements.systolic)}
          data-bgcolor={bpResult.backgroundColor}
        >
          <span
            onClick={() => handleChartClick(measurements.systolic, patient.id)}
            style={{
              cursor: "default",
              color: bpResult.color
            }}
          >
            {checkSignalQuality(measurements.systolic.readingAccuracy)}
            {displayValue(measurements.systolic.value, 'round') !== '-' && displayValue(measurements.diastolic.value, 'round') !== '-'
              ? `${displayValue(measurements.systolic.value, 'round')}/${displayValue(measurements.diastolic.value, 'round')}${measurements.systolic.unit}`
              : (measurements.systolic.value === "unacceptable") ? displayValue(measurements.systolic.value, 'round') : '-'}
          </span>
        </OverlayTrigger>
      ),
      bp_sort: measurements.systolic.value && measurements.diastolic.value
        ? `${displayValue(measurements.systolic.value, 'round')}/${displayValue(measurements.diastolic.value, 'round')}`
        : '',
      outOfLimit_sort: (rowHasOutOfLimit || alertCount !== 0) ? 1 : 0,
      enrollment_sort: patient.activelyEnrolledFlg ? 1 : 0
    };
  }), [filteredPatientList, patientList, getMeasurement, batteryLevels, loading, userUnitPreference, alertsMap, patientReadings]);

  return (
    <div className={styles.MainContent}>
      <div className={styles.MiddleContent}>
        <div className={`${styles.CardContent}`}>
          <div className={styles.CardRow}>
            <Cards icon={<NewPatientCardIcon color={filterCard === "New Patients" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("new")} text="New Patients" onClick={newPatientFilter} />
            <Cards icon={<PatientDischargeIcon color={filterCard === "Patients to Be Discharged" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("discharge")} text="Patients to Be Discharged" onClick={dischargeFilter}/>
            <Cards icon={<PatientAlarmIcon color={filterCard === "Patient Alerts" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("alert")} text="Patient Alerts" onClick={patientAlarmsFilter} />
            <Cards icon={<ScheduleAppointmentIcon color={filterCard === "Appointments" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("appointment")} text="Appointments" onClick={appointmentFilter} />
          </div>
          <div className={styles.CardRow}>
            <Cards icon={<NewMessagesIcon color={filterCard === "New Messages" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("unreadMessage")} text="New Messages" onClick={unreadMessageFilter} />
            <Cards icon={<NewMedicalReportIcon color={filterCard === "Unresolved Medical Report" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("reports")} text="Unresolved Medical Report" onClick={reportFilter} />
            <Cards icon={<DevicesNeedsServicingIcon color={filterCard === "Devices Need Servicing" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("service")} text="Devices Need Servicing" onClick={serviceNeededFilter} />
            <Cards icon={<PatientsUnderMonitoringIcon color={filterCard === "Patients under monitoring" ? "#1D5769" : "#4F4F4F"} />} count={filterCount("activelyEnrolled")} text="Patients Under Monitoring" onClick={activelyEnrolledFilter} />
          </div>
        </div>
        <div className={`${styles.AppointmentContent}`}>
          <AppointmentsContainer appointments={appointments} />
        </div>
      </div>

      <div className={styles.GridContent}>
        <FilterManagementView
          isOrganization
          isGroup
          isSubGroup
          isTeam
          showAddButton={false}
          showDeleteButton={filterCard === "Patients to Be Discharged"}
          deleteButtonLabel={"Discharge"}
          onDeleteHandler={onDischargeHandler}
          cardFilter
          customDeleteModal={showDischargeModal}
        />
        <div className={managementStyles.GridContainer}>
          <PerinHealthGrid
            dataList={gridData}
            headers={headers}
            iconMapping={iconMapping}
            accounts={true}
            showHeaderFilter
            showProfilePicture="patient"
            gridHeight="52vh"
            rowValueAction="patient"
            onRowValueClick={handlePatientSelection}
            showStatusBars={true}
            showCheckbox={filterCard === "Patients to Be Discharged"}
          />
        </div>
      </div>

      <ChartDetailView
        isModalVisible={chartDetailVisible}
        handleCloseModal={closeChartDetail}
        modalData={chartDetailModalData}
      />
    </div>
  );
};

export default OverviewDashboard;
